import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Link,
  Paper,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import HistoryIcon from '@mui/icons-material/History';
import FolderIcon from '@mui/icons-material/Folder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import TuneIcon from '@mui/icons-material/Tune';
import BarChartIcon from '@mui/icons-material/BarChart';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SimilarIcon from '@mui/icons-material/FindReplace';
import MultipleStopIcon from '@mui/icons-material/MultipleStop';
import DeleteIcon from '@mui/icons-material/Delete';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import { AiActions } from '../../../Services/_actions/Ai/ai.actions.ts';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import CommonTable from '../../Shared/Tables/TableV1.tsx';
import MultiKnowledgeBaseSearch from './MultiKnowledgeBaseSearch.tsx';
import SearchBox from '../../FlowBuilder/Common/SearchBox/index.tsx';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';
import { useRecoilState } from 'recoil';
import { GetFileListAtom } from '../../../Services/_states/VectorDatabase/index.ts';

const KnowledgeBaseDetailsPage = () => {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const {
    getKnowledgeBaseDetails,
    getFileList,
    uploadFile,
    searchKnowledgeBase,
    searchMultiKnowledgeBase,
    retrieveChunks,
    getSimilarChunks,
    getOrganizationSearchStats,
    listKnowledgeBases,
    deleteFile,
  } = AiActions();
  const location = useLocation();
  const scrollRef = useRef(null);

  const { knowledge_base_id, organization_id, knowledgeBase } = location.state || {};

  // Existing states
  const [details, setDetails] = useState(knowledgeBase ? { knowledge_base: knowledgeBase } : null);
  const [loading, setLoading] = useState(!knowledgeBase);

  const [files, setFiles] = useRecoilState(GetFileListAtom);
  // const [files, setFiles] = useState([]);
  const [currentView, setCurrentView] = useState('all');
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Enhanced search states
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    topK: 5,
    scoreThreshold: 0.7,
    includeMetadata: true,
  });
  const [searchStats, setSearchStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [similarFind, setSimilarFind] = useState([]);
  // Helper function to clean search text
  const cleanSearchText = (text) => {
    if (!text) return '';

    return text
      .replace(/\s+/g, ' ')
      .replace(/[^\w\s.,!?;:()\-]/g, '')
      .trim();
  };

  const fetchDetails = async () => {
    if (!knowledge_base_id || !organization_id) {
      showSnackBar('Missing knowledge base or organization ID', 'error');
      return;
    }
    setLoading(true);
    try {
      const response = await getKnowledgeBaseDetails(organization_id, knowledge_base_id, '', false);
      setDetails(response);
    } catch (err) {
      console.error(err);
      showSnackBar('Failed to load knowledge base details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async (confirmed: boolean) => {
    if (confirmed && selectedFile) {
      await handleDeleteFile(selectedFile);
    }
    setConfirmOpen(false);
    setSelectedFile(null);
  };

  const handleDeleteFile = async (file) => {
    try {
      await deleteFile(organization_id, knowledge_base_id, file.file_id);
      showSnackBar(`File "${file.file_name}" deleted successfully`, 'success');
      fetchFiles();
    } catch (error) {
      showSnackBar('Failed to delete file', 'error');
    }
  };

  const fetchFiles = async () => {
    if (!knowledge_base_id || !organization_id) {
      return;
    }
    setFilesLoading(true);
    try {
      const response = await getFileList(organization_id, knowledge_base_id);
      setFiles(response?.files || []);
    } catch (err) {
      showSnackBar('Failed to load files', 'error');
    } finally {
      setFilesLoading(false);
    }
  };

  const fetchSearchStats = async () => {
    if (!organization_id) return;

    setStatsLoading(true);
    try {
      const response = await getOrganizationSearchStats(organization_id);
      setSearchStats(response);
    } catch (err) {
      showSnackBar('Failed to load search statistics', 'error');
    } finally {
      setStatsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      showSnackBar('Please enter a search query', 'warning');
      return;
    }

    setSearchLoading(true);
    try {
      const searchData = {
        query: searchQuery.trim(),
        top_k: searchFilters.topK,
        score_threshold: searchFilters.scoreThreshold,
        include_metadata: searchFilters.includeMetadata,
      };

      const response = await searchKnowledgeBase(organization_id, knowledge_base_id, searchData);

      // Handle the response structure - it might be nested
      let results = [];
      if (response?.results) {
        results = response.results;
      } else if (Array.isArray(response)) {
        results = response;
      } else if (response?.data?.results) {
        results = response.data.results;
      }

      // Process and enhance results
      const processedResults = results.map((result, index) => ({
        ...result,
        // Ensure we have all necessary fields
        chunk_id: result.chunk_id || result.vector_id || `chunk_${index}`,
        similarity_score: result.similarity_score || result.score || 0,
        text: result.text || result.content || '',
        file_name: result.file_name || result.metadata?.file_name || 'Unknown File',
        file_type: result.file_type || result.metadata?.file_type || 'unknown',
        chunk_index: result.chunk_index || result.metadata?.chunk_index || index,
        displayText: cleanSearchText(result.text || result.content || ''),
      }));

      setSearchResults(processedResults);

      if (processedResults.length === 0) {
        showSnackBar(
          `No results found for "${searchQuery}". Try adjusting your search terms or filters.`,
          'info'
        );
      } else {
        showSnackBar(`Found ${processedResults.length} result(s) for "${searchQuery}"`, 'success');
      }

      // Log search metadata if available
      if (response?.search_metadata) {
        console.log('Search metadata:', response.search_metadata);
      }
    } catch (err) {
      console.error('Search failed:', err);
      showSnackBar('Search failed. Please try again.', 'error');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Enhanced similar chunks handler
  const handleGetSimilarChunks = async (chunkId) => {
    if (!chunkId) {
      showSnackBar('No chunk ID available', 'warning');
      return;
    }

    try {
      setSearchLoading(true);
      const response = await getSimilarChunks(organization_id, knowledge_base_id, chunkId, {
        top_k: 5,
        include_metadata: true,
      });

      setSimilarFind(response?.similar_chunks || []);

      if (response?.similar_chunks && response.similar_chunks.length > 0) {
        // Process similar chunks results
        const processedSimilar = response.similar_chunks.map((result, index) => ({
          ...result,
          chunk_id: result.chunk_id || result.vector_id || `similar_${index}`,
          similarity_score: result.similarity_score || result.score || 0,
          text: result.text || result.content || result.chunk_text || '',
          file_name: result.file_name || result.metadata?.file_name || 'Unknown File',
          displayText: cleanSearchText(result.text || result.content || result.chunk_text || ''),
        }));

        // Replace current search results with similar chunks
        setSearchResults(processedSimilar);
        setSearchQuery(`Similar to chunk: ${chunkId.substring(0, 8)}...`);
        showSnackBar(`Found ${response?.total_similar_chunks} similar chunks`, 'success');
      } else {
        showSnackBar('No similar chunks found', 'info');
      }
    } catch (err) {
      console.error('Failed to get similar chunks:', err);
      showSnackBar('Failed to get similar chunks', 'error');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleRetrieveChunks = async (chunkIds) => {
    if (!chunkIds || chunkIds.length === 0) return;

    try {
      const response = await retrieveChunks(organization_id, {
        chunk_ids: chunkIds,
        include_metadata: true,
      });

      console.log('Retrieved chunks:', response);
      return response;
    } catch (err) {
      console.error('Failed to retrieve chunks:', err);
      showSnackBar('Failed to retrieve chunks', 'error');
    }
  };

  // Navigation handlers
  const handleFileClick = (file) => {
    if (!file.file_id) {
      showSnackBar('File ID not available', 'error');
      return;
    }

    navigate('/r/vector/knowledge-base/file-details', {
      state: {
        file_id: file.file_id,
        knowledge_base_id,
        organization_id,
        fileName: file.file_name,
        knowledgeBaseName: kb.name,
      },
    });
  };

  const handleNavigateToMultiSearch = () => {
    navigate('/r/vector/multi-search', {
      state: {
        organization_id,
        preselected_kb_ids: [knowledge_base_id],
        knowledge_base_name: kb.name,
      },
    });
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const getFileTypeIcon = (fileType) => {
    const extension = fileType?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📄';
      case 'xlsx':
      case 'xls':
        return '📊';
      case 'docx':
      case 'doc':
        return '📝';
      case 'txt':
        return '📃';
      case 'csv':
        return '📈';
      default:
        return '📁';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const validateFile = (file) => {
    const allowedTypes = ['.xlsx', '.docx', '.doc'];
    const maxSize = 15 * 1024 * 1024;
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(fileExtension)) {
      return { valid: false, error: `File type ${fileExtension} not supported` };
    }

    if (file.size > maxSize) {
      return { valid: false, error: 'File size exceeds 15MB limit' };
    }

    return { valid: true };
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFileSelection(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFileSelection(selectedFiles);
  };

  const handleFileSelection = (fileList) => {
    const validFiles = [];
    const errors = [];

    fileList.forEach((file) => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    });

    if (errors.length > 0) {
      showSnackBar(errors.join(', '), 'error');
    }

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      setUploadDialogOpen(true);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    const uploadPromises = selectedFiles.map(async (file) => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('chunk_size', '1000');
        formData.append('chunk_overlap', '200');

        const response = await uploadFile(organization_id, knowledge_base_id, formData);
        return { success: true, fileName: file.name, response };
      } catch (error) {
        return { success: false, fileName: file.name, error };
      }
    });

    try {
      const results = await Promise.all(uploadPromises);
      const successful = results.filter((r) => r.success);
      const failed = results.filter((r) => !r.success);

      if (successful.length > 0) {
        showSnackBar(`Successfully uploaded ${successful.length} file(s)`, 'success');
        fetchFiles();
      }

      if (failed.length > 0) {
        showSnackBar(`Failed to upload ${failed.length} file(s)`, 'error');
      }

      setUploadDialogOpen(false);
      setSelectedFiles([]);
    } catch (error) {
      showSnackBar('Upload failed. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFiles([]);
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 3 && !searchStats) {
      fetchSearchStats();
    }
  };

  useEffect(() => {
    fetchDetails();
    fetchFiles();
  }, [knowledge_base_id, organization_id]);

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      onClick: () => navigate('/'),
    },
    {
      label: 'Knowledge Base List',
      onClick: () => navigate('/r/vector/add-knowledge'),
    },
    {
      label: 'Details',
    },
  ];

  const getDisplayData = () => {
    if (currentView === 'recent') {
      return details?.recent_files || [];
    }
    return files;
  };

  const displayData = getDisplayData();

  const filesColumns = [
    {
      accessorKey: 'file_name',
      header: 'File Name',
      Cell: ({ cell, row }) => (
        <Link
          color="primary"
          underline="hover"
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1 }}
          onClick={() => handleFileClick(row.original)}
        >
          <InsertDriveFileIcon fontSize="small" />
          {cell.getValue()}
        </Link>
      ),
    },
    {
      accessorKey: 'file_size',
      header: 'Size (KB)',
      Cell: ({ cell }) => (cell.getValue() ? (cell.getValue() / 1024).toFixed(1) : '-'),
    },
    {
      accessorKey: 'chunk_statistics.total_chunks',
      header: 'Total Chunks',
      Cell: ({ row }) =>
        row.original.chunk_statistics?.total_chunks ||
        row.original.metadata?.total_chunks ||
        row.original.total_chunks ||
        '-',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()}
          size="small"
          color={cell.getValue() === 'completed' ? 'success' : 'warning'}
        />
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Uploaded At',
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (!value) return '-';

        const date = new Date(value);
        // add 330 minutes (5h 30m)
        const adjusted = new Date(date.getTime() + 330 * 60 * 1000);

        return adjusted.toLocaleString('en-IN', {
          dateStyle: 'medium',
          timeStyle: 'short',
        });
      },
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      Cell: ({ row }) => (
        <IconButton
          size="small"
          color="error"
          // onClick={() => handleDeleteFile(row.original)}
          onClick={() => {
            setSelectedFile(row.original);
            setConfirmOpen(true);
          }}
          title="Delete file"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  if (loading && !details) {
    return (
      <Box sx={{ p: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>
        <Typography variant="h6">Loading Knowledge Base Details...</Typography>
        <CircularProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!details && !knowledgeBase) {
    return (
      <Box sx={{ p: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>
        <Typography variant="h6" color="error">
          No data found.
        </Typography>
      </Box>
    );
  }

  const kb = details?.knowledge_base || knowledgeBase || {};

  const stats = {
    totalFiles: details?.statistics?.total_files ?? files?.length ?? 'N/A',
    totalSize: details?.statistics?.total_size
      ? (details.statistics.total_size / 1024).toFixed(1)
      : 'N/A',
    totalChunks: details?.statistics?.total_chunks ?? 'N/A',
    pineconeVectors: details?.pinecone_stats?.total_vector_count ?? 'N/A',
    dimension: details?.pinecone_stats?.dimension ?? 'N/A',
    metric: details?.pinecone_stats?.metric ?? 'N/A',
    state: details?.pinecone_stats?.status?.state ?? 'N/A',
    status: kb.status?.toUpperCase() ?? 'N/A',
    pineconeIndex: kb.index_name ?? 'N/A',
  };

  const handleScroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ p: 2, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Breadcrumb */}
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item) => item?.onClick?.()}
      />

      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        mt={1}
        mb={2}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#1e293b' }}>
            {kb.name || 'Knowledge Base Details'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {kb.description || 'Knowledge Base Details Overview'}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Box textAlign="right">
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              Index
            </Typography>
            <Typography>{stats.pineconeIndex}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Scrollable Summary Cards */}
      <Box sx={{ position: 'relative', my: 2 }}>
        <IconButton
          onClick={() => handleScroll('left')}
          sx={{
            position: 'absolute',
            left: -8,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            bgcolor: 'white',
            boxShadow: 1,
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            px: 4,
          }}
        >
          {[
            { label: 'Total Files', value: stats.totalFiles },
            { label: 'Total Size (KB)', value: stats.totalSize },
            { label: 'Total Chunks', value: stats.totalChunks },
            { label: 'Vectors', value: stats.pineconeVectors },
            { label: 'Dimension', value: stats.dimension },
            { label: 'Metric', value: stats.metric },
            { label: 'Status', value: stats.status },
            { label: 'State', value: stats.state, isChip: true },
          ].map((stat, idx) => (
            <Box
              key={idx}
              sx={{
                minWidth: 160,
                mr: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: '#ffffff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                textAlign: 'center',
                flexShrink: 0,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {stat.label}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {stat.isChip ? (
                  <Chip
                    label={stat.value}
                    color={stat.value === 'Ready' ? 'success' : 'warning'}
                    size="small"
                  />
                ) : (
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: stat.value === 'N/A' ? 'text.disabled' : 'text.primary' }}
                  >
                    {stat.value}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        <IconButton
          onClick={() => handleScroll('right')}
          sx={{
            position: 'absolute',
            right: -8,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            bgcolor: 'white',
            boxShadow: 1,
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="knowledge base tabs">
          <Tab
            label="Files"
            icon={<FolderIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            label="Search"
            icon={<SearchIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            label="Multi-Search"
            icon={<MultipleStopIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            label="Analytics"
            icon={<BarChartIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
        </Tabs>
      </Box>

      {/* Files Tab */}
      <TabPanel value={activeTab} index={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" fontWeight={600}>
              Files ({displayData.length})
            </Typography>
            {filesLoading && <CircularProgress size={20} />}
          </Box>

          <Stack direction="row" spacing={1}>
            {displayData.length > 0 && (
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
                sx={{
                  bgcolor: 'primary',
                  '&:hover': { bgcolor: 'primary' },
                  textTransform: 'none',
                }}
              >
                Upload More Files
              </Button>
            )}
            <Button
              variant={currentView === 'all' ? 'contained' : 'outlined'}
              size="small"
              startIcon={<FolderIcon />}
              onClick={() => handleViewChange('all')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 120,
              }}
            >
              All Files
            </Button>
            <Button
              variant={currentView === 'recent' ? 'contained' : 'outlined'}
              size="small"
              startIcon={<HistoryIcon />}
              onClick={() => handleViewChange('recent')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 120,
              }}
            >
              Recent Files
            </Button>
          </Stack>
        </Box>

        {displayData.length > 0 ? (
          <CommonTable
            columns={filesColumns}
            data={displayData}
            enableStickyHeader
            enableTableFooter={false}
            enableBottomToolbar={false}
          />
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {filesLoading
                ? 'Loading files...'
                : currentView === 'recent'
                  ? 'No recent files available.'
                  : 'No files uploaded yet.'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {currentView === 'recent'
                ? 'Recent files will appear here once available.'
                : 'Upload files to this knowledge base to get started.'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
              sx={{
                bgcolor: '#3b82f6',
                '&:hover': { bgcolor: '#2563eb' },
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Upload Files
            </Button>
          </Paper>
        )}
      </TabPanel>

      {/* Single KB Search Tab */}
      <TabPanel value={activeTab} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <FindInPageIcon color="primary" />
                Search This Knowledge Base
              </Typography>

              <Stack spacing={3}>
                {/* Search Box */}
                <TextField
                  type="text"
                  autoFocus
                  fullWidth
                  placeholder="Enter your search query..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="contained"
                          onClick={handleSearch}
                          disabled={searchLoading || !searchQuery.trim()}
                          startIcon={
                            searchLoading ? <CircularProgress size={16} /> : <SearchIcon />
                          }
                          sx={{ ml: 1 }}
                        >
                          {searchLoading ? 'Searching...' : 'Search'}
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Results Section */}
                {(similarFind.length > 0 ? similarFind : searchResults).length > 0 && (
                  <Box sx={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {similarFind.length > 0
                          ? `Similar Results (${similarFind.length})`
                          : `Search Results (${searchResults.length})`}
                      </Typography>
                      <Chip
                        label={similarFind.length > 0 ? `Source Chunk` : `Query: "${searchQuery}"`}
                        variant="outlined"
                        size="small"
                        sx={{
                          maxWidth: 200,
                          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                        }}
                      />
                    </Box>

                    {/* Results List */}
                    <Stack spacing={2}>
                      {(similarFind.length > 0 ? similarFind : searchResults).map(
                        (result, index) => (
                          <Card
                            key={result.chunk_id || result.vector_id || index}
                            sx={{
                              borderLeft: '4px solid #3b82f6',
                              '&:hover': { boxShadow: 3 },
                            }}
                          >
                            <CardContent>
                              {/* Header */}
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                mb={2}
                              >
                                <Box flex={1}>
                                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                                    {result.file_name ||
                                      result.metadata?.file_name ||
                                      `Document ${index + 1}`}
                                  </Typography>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                      label={result.file_type?.toUpperCase() || 'FILE'}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                      Chunk{' '}
                                      {result.chunk_index || result.metadata?.chunk_index || 'N/A'}
                                    </Typography>
                                  </Stack>
                                </Box>
                                <Chip
                                  label={`${(result.similarity_score * 100).toFixed(1)}%`}
                                  size="small"
                                  color={
                                    result.similarity_score > 0.8
                                      ? 'success'
                                      : result.similarity_score > 0.7
                                        ? 'warning'
                                        : 'default'
                                  }
                                  sx={{ ml: 2 }}
                                />
                              </Box>

                              {/* Content */}
                              <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{
                                  mb: 2,
                                  lineHeight: 1.6,
                                  display: '-webkit-box',
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {result.displayText ||
                                  result.text ||
                                  result.content ||
                                  'No content available'}
                              </Typography>

                              {/* Metadata */}
                              {(result.metadata || result.created_at) && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block' }}
                                  >
                                    {result.created_at && (
                                      <>
                                        Created: {new Date(result.created_at).toLocaleDateString()}
                                      </>
                                    )}
                                    {result.metadata?.chunk_length && (
                                      <> • Length: {result.metadata.chunk_length} chars</>
                                    )}
                                  </Typography>
                                </Box>
                              )}

                              {/* Actions */}
                              <Box display="flex" gap={1}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<SimilarIcon />}
                                  onClick={() => handleGetSimilarChunks(result.chunk_id)}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Find Similar
                                </Button>
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={<ContentCopyIcon />}
                                  onClick={() => {
                                    navigator.clipboard.writeText(result.text || result.content);
                                    showSnackBar('Content copied to clipboard', 'success');
                                  }}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Copy Text
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        )
                      )}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <TuneIcon color="primary" />
                Search Filters
              </Typography>

              <Stack spacing={3}>
                <TextField
                  label="Max Results"
                  type="number"
                  size="small"
                  value={searchFilters.topK}
                  onChange={(e) =>
                    setSearchFilters((prev) => ({ ...prev, topK: parseInt(e.target.value) || 5 }))
                  }
                  inputProps={{ min: 1, max: 20 }}
                  helperText="Maximum number of results (1-20)"
                />
                <TextField
                  label="Score Threshold"
                  type="number"
                  size="small"
                  value={searchFilters.scoreThreshold}
                  onChange={(e) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      scoreThreshold: parseFloat(e.target.value) || 0.7,
                    }))
                  }
                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                  helperText="Minimum similarity score (0-1)"
                />

                {/* Quick Stats */}
                {searchResults.length > 0 && (
                  <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1, mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Search Statistics
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Results: {searchResults.length}
                      <br />
                      Avg Score:{' '}
                      {(
                        (searchResults.reduce((acc, r) => acc + r.similarity_score, 0) /
                          searchResults.length) *
                        100
                      ).toFixed(1)}
                      %<br />
                      Best Match:{' '}
                      {(Math.max(...searchResults.map((r) => r.similarity_score)) * 100).toFixed(1)}
                      %
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Multi-KB Search Tab */}
      <TabPanel value={activeTab} index={2}>
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Multi-Knowledge Base Search</strong>
              <br />
              Search across multiple knowledge bases in your organization. This knowledge base (
              {kb.name}) is pre-selected.
            </Typography>
          </Alert>
        </Box>

        <MultiKnowledgeBaseSearch
          organizationId={organization_id}
          availableKnowledgeBases={[]}
          preselectedKBs={[knowledge_base_id]}
          currentKBName={kb.name}
        />
      </TabPanel>

      {/* Enhanced Analytics Tab */}
      <TabPanel value={activeTab} index={3}>
        <Grid container spacing={3}>
          {/* Organization Overview */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <BarChartIcon color="primary" />
                Organization Analytics Overview
              </Typography>

              {statsLoading ? (
                <Box display="flex" alignItems="center" gap={2} py={4}>
                  <CircularProgress size={24} />
                  <Typography>Loading analytics...</Typography>
                </Box>
              ) : searchStats ? (
                <>
                  {/* Organization Stats Cards */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center" sx={{ p: 3, bgcolor: '#f0f9ff', borderRadius: 2 }}>
                        <Typography variant="h3" fontWeight={700} color="#0ea5e9">
                          {searchStats.organization_stats?.total_knowledge_bases || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Knowledge Bases
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center" sx={{ p: 3, bgcolor: '#f0fdf4', borderRadius: 2 }}>
                        <Typography variant="h3" fontWeight={700} color="#22c55e">
                          {searchStats.organization_stats?.total_files || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Files
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center" sx={{ p: 3, bgcolor: '#fef3c7', borderRadius: 2 }}>
                        <Typography variant="h3" fontWeight={700} color="#f59e0b">
                          {searchStats.organization_stats?.total_chunks || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Chunks
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center" sx={{ p: 3, bgcolor: '#fef2f2', borderRadius: 2 }}>
                        <Typography variant="h3" fontWeight={700} color="#ef4444">
                          {searchStats.organization_stats?.total_size
                            ? `${(searchStats.organization_stats.total_size / 1024 / 1024).toFixed(1)} MB`
                            : 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Size
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Current Knowledge Base Details */}
                  <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
                    Current Knowledge Base: {kb.name}
                  </Typography>

                  {searchStats.knowledge_base_stats?.map(
                    (kbStat, index) =>
                      kbStat.knowledge_base_id === knowledge_base_id && (
                        <Grid container spacing={3} key={index}>
                          {/* MongoDB Stats */}
                          <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%' }}>
                              <CardContent>
                                <Typography variant="h6" gutterBottom color="primary">
                                  📊 Content Statistics
                                </Typography>
                                <Stack spacing={2}>
                                  <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2">Files:</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {kbStat.mongo_stats?.total_files || 0}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2">Chunks:</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {kbStat.mongo_stats?.total_chunks || 0}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2">Size:</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {kbStat.mongo_stats?.total_size
                                        ? `${(kbStat.mongo_stats.total_size / 1024).toFixed(1)} KB`
                                        : 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2">Embedding Model:</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {kbStat.embedding_model || 'N/A'}
                                    </Typography>
                                  </Box>

                                  {/* Chunk Status Breakdown */}
                                  {kbStat.mongo_stats?.chunk_status_breakdown && (
                                    <Box sx={{ mt: 2 }}>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        gutterBottom
                                      >
                                        Chunk Processing Status:
                                      </Typography>
                                      {Object.entries(
                                        kbStat.mongo_stats.chunk_status_breakdown
                                      ).map(([status, count]) => (
                                        <Box
                                          key={status}
                                          display="flex"
                                          justifyContent="space-between"
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{ textTransform: 'capitalize' }}
                                          >
                                            {status}:
                                          </Typography>
                                          <Chip
                                            label={count}
                                            size="small"
                                            color={status === 'completed' ? 'success' : 'warning'}
                                          />
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          {/* Pinecone Stats */}
                          <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%' }}>
                              <CardContent>
                                <Typography variant="h6" gutterBottom color="primary">
                                  🔍 Vector Search Statistics
                                </Typography>
                                {kbStat.pinecone_stats?.success ? (
                                  <Stack spacing={2}>
                                    <Box display="flex" justifyContent="space-between">
                                      <Typography variant="body2">Index Name:</Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        sx={{ fontSize: '0.8rem' }}
                                      >
                                        {kbStat.pinecone_stats.index_name}
                                      </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                      <Typography variant="body2">Vector Count:</Typography>
                                      <Typography variant="body2" fontWeight={600}>
                                        {kbStat.pinecone_stats.total_vector_count?.toLocaleString() ||
                                          0}
                                      </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                      <Typography variant="body2">Dimension:</Typography>
                                      <Typography variant="body2" fontWeight={600}>
                                        {kbStat.pinecone_stats.dimension || 'N/A'}
                                      </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                      <Typography variant="body2">Metric:</Typography>
                                      <Typography variant="body2" fontWeight={600}>
                                        {kbStat.pinecone_stats.metric || 'N/A'}
                                      </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                      <Typography variant="body2">Index Fullness:</Typography>
                                      <Typography variant="body2" fontWeight={600}>
                                        {(
                                          (kbStat.pinecone_stats.index_fullness || 0) * 100
                                        ).toFixed(2)}
                                        %
                                      </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                      <Typography variant="body2">Status:</Typography>
                                      <Chip
                                        label={kbStat.pinecone_stats.status?.state || 'Unknown'}
                                        size="small"
                                        color={
                                          kbStat.pinecone_stats.status?.ready
                                            ? 'success'
                                            : 'warning'
                                        }
                                      />
                                    </Box>

                                    {/* Namespace breakdown */}
                                    {kbStat.pinecone_stats.namespaces && (
                                      <Box sx={{ mt: 2 }}>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          gutterBottom
                                        >
                                          Namespaces:
                                        </Typography>
                                        {Object.entries(kbStat.pinecone_stats.namespaces).map(
                                          ([namespace, data]) => (
                                            <Box
                                              key={namespace}
                                              display="flex"
                                              justifyContent="space-between"
                                            >
                                              <Typography variant="caption">
                                                {namespace || 'Default'}:
                                              </Typography>
                                              <Typography variant="caption" fontWeight={600}>
                                                {data.vector_count?.toLocaleString()} vectors
                                              </Typography>
                                            </Box>
                                          )
                                        )}
                                      </Box>
                                    )}
                                  </Stack>
                                ) : (
                                  <Alert severity="warning">Statistics unavailable</Alert>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      )
                  )}

                  {/* Search Capabilities Summary */}
                  {searchStats.search_capabilities && (
                    <Grid item xs={12} sx={{ mt: 3 }}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom color="primary">
                            🎯 Search Capabilities
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <Box
                                textAlign="center"
                                sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}
                              >
                                <Typography variant="h4" fontWeight={600} color="primary">
                                  {searchStats.search_capabilities.total_searchable_kbs || 0}
                                </Typography>
                                <Typography variant="body2">Searchable KBs</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Box
                                textAlign="center"
                                sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}
                              >
                                <Typography variant="h4" fontWeight={600} color="primary">
                                  {searchStats.search_capabilities.total_vectors?.toLocaleString() ||
                                    0}
                                </Typography>
                                <Typography variant="body2">Total Vectors</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Box
                                textAlign="center"
                                sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}
                              >
                                <Typography variant="h4" fontWeight={600} color="primary">
                                  {searchStats.search_capabilities.available_embedding_models
                                    ?.length || 0}
                                </Typography>
                                <Typography variant="body2">Embedding Models</Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          {searchStats.search_capabilities.available_embedding_models && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Available Models:
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                {searchStats.search_capabilities.available_embedding_models.map(
                                  (model, index) => (
                                    <Chip
                                      key={index}
                                      label={model}
                                      size="small"
                                      variant="outlined"
                                      sx={{ mb: 1 }}
                                    />
                                  )
                                )}
                              </Stack>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </>
              ) : (
                <Alert severity="info">
                  No analytics data available yet. Analytics will appear here once you have uploaded
                  files and performed searches.
                </Alert>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Upload Modal */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleCloseUploadDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <CloudUploadIcon color="primary" />
              <Typography variant="h6">Upload Files</Typography>
            </Box>
            <IconButton onClick={handleCloseUploadDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pb: 2 }}>
          {/* Drag & Drop Upload Area in Modal */}
          <Paper
            sx={{
              p: 4,
              border: dragActive ? '2px solid #3b82f6' : '2px dashed #cbd5e1',
              borderRadius: 2,
              bgcolor: dragActive ? '#f0f9ff' : '#fafafa',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              textAlign: 'center',
              mb: selectedFiles.length > 0 ? 3 : 0,
              '&:hover': {
                borderColor: '#3b82f6',
                bgcolor: '#f8fafc',
              },
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-upload-input').click()}
          >
            <input
              id="file-upload-input"
              type="file"
              multiple
              accept=".xlsx,.docx,.doc"
              onChange={handleFileInput}
              style={{ display: 'none' }}
            />
            <CloudUploadIcon
              sx={{
                fontSize: 48,
                color: dragActive ? '#3b82f6' : '#6b7280',
                mb: 2,
              }}
            />
            <Typography variant="h6" fontWeight={600} sx={{ color: '#1e293b', mb: 1 }}>
              Drag & Drop or Click to Upload
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Supports XLSX, DOCX, DOC. Max size 15MB.
            </Typography>
            <Button
              variant="contained"
              sx={{
                bgcolor: '#3b82f6',
                '&:hover': { bgcolor: '#2563eb' },
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              UPLOAD FILE
            </Button>
          </Paper>

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Selected Files ({selectedFiles.length})
              </Typography>

              <Stack spacing={2} sx={{ mb: 3, maxHeight: 300, overflow: 'auto' }}>
                {selectedFiles.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      bgcolor: '#f8fafc',
                      borderRadius: 1,
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box sx={{ fontSize: '1.5rem' }}>
                        {getFileTypeIcon(file.name.split('.').pop())}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeFile(index)}
                      sx={{ color: '#ef4444' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Files will be uploaded to <strong>{kb.name}</strong> and processed into chunks for
                vector search.
              </Typography>

              <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                <Typography variant="body2">
                  <br />• Processing may take a few minutes per file
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseUploadDialog} variant="outlined" disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || selectedFiles.length === 0}
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            sx={{
              bgcolor: '#3b82f6',
              '&:hover': { bgcolor: '#2563eb' },
            }}
          >
            {uploading
              ? 'Uploading...'
              : selectedFiles.length > 0
                ? `Upload ${selectedFiles.length} File(s)`
                : 'Upload Files'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmBox
        open={confirmOpen}
        onClose={handleConfirmDelete}
        options={{
          title: 'Delete File',
          text: `Are you sure you want to delete "${selectedFile?.file_name}"?`,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          confirmButtonColor: 'error',
          cancelButtonColor: 'default',
        }}
      />
    </Box>
  );
};

export default KnowledgeBaseDetailsPage;
